import { useEffect, useState } from 'react';

import type { DoohReportLocation } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

export type WalkingRoute = {
  coordinates: [number, number][];
};

type DirectionsResponse = {
  routes?: Array<{ geometry: { coordinates: [number, number][] } }>;
};

function straightLine(origin: DoohReportLocation, destination: DoohReportLocation): WalkingRoute {
  return {
    coordinates: [
      [origin.lng, origin.lat],
      [destination.lng, destination.lat]
    ]
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Fetches a walking route between two points from the Mapbox Directions API.
 * Falls back to a straight line between the points when the
 * request fails. Only fetches while `enabled` is true, so rows don't call the
 * Directions API before their route modal is actually opened.
 */
export default function useWalkingRoute(
  origin: DoohReportLocation,
  destination: DoohReportLocation,
  enabled: boolean
): WalkingRoute | null {
  const [route, setRoute] = useState<WalkingRoute | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const controller = new AbortController();
    const coordinates = `${origin.lng},${origin.lat};${destination.lng},${destination.lat}`;
    const url =
      `https://api.mapbox.com/directions/v5/mapbox/walking/${coordinates}` +
      `?geometries=geojson&overview=full&access_token=${taptap.config.mapbox.accessToken}`;

    fetch(url, { signal: controller.signal })
      .then(response => (response.ok ? (response.json() as Promise<DirectionsResponse>) : Promise.reject(response)))
      .then(data => {
        const [firstRoute] = data.routes ?? [];
        setRoute(firstRoute ? { coordinates: firstRoute.geometry.coordinates } : straightLine(origin, destination));
      })
      .catch((error: unknown) => {
        if (!isAbortError(error)) {
          setRoute(straightLine(origin, destination));
        }
      });

    return () => controller.abort();
  }, [origin, destination, enabled]);

  return route;
}
